import { useState } from "react";
import { UserName } from "@/components/badges/UserName";
import { formatDistanceToNow } from "date-fns";
import { Clock, Loader2, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useSentConnectionRequests, useCancelConnectionRequest } from "@/hooks/useCommunityConnections";

const INITIAL_DISPLAY_COUNT = 10;

export function SentRequestsList() {
  const { data: requests, isLoading } = useSentConnectionRequests();
  const cancelRequest = useCancelConnectionRequest();
  const [showAll, setShowAll] = useState(false);

  const count = requests?.length || 0;
  const displayedRequests = showAll ? requests : requests?.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = count > INITIAL_DISPLAY_COUNT;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (count === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="h-4 w-4" />
            Pending Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">No pending requests</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-warning" />
          Pending Requests ({count})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayedRequests?.map((request) => {
          const recipient = request.recipient;
          if (!recipient) return null;

          const isCancelling = cancelRequest.isPending && cancelRequest.variables === request.id;

          return (
            <div
              key={request.id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={recipient.avatar_url || undefined} />
                <AvatarFallback className="bg-muted text-muted-foreground text-sm">
                  {recipient.full_name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate"><UserName userId={recipient.id} name={recipient.full_name} /></p>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      recipient.role === "mentor" ? "badge-mentor" : "badge-entrepreneur"
                    }`}
                  >
                    {recipient.role === "mentor" ? "Mentor" : "Entrepreneur"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Sent {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                </p>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive shrink-0"
                onClick={() => cancelRequest.mutate(request.id)}
                disabled={cancelRequest.isPending}
              >
                {isCancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Cancel Request"
                )}
              </Button>
            </div>
          );
        })}

        {hasMore && !showAll && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2"
            onClick={() => setShowAll(true)}
          >
            View All ({count - INITIAL_DISPLAY_COUNT} more)
          </Button>
        )}

        {showAll && hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2"
            onClick={() => setShowAll(false)}
          >
            Show Less
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default SentRequestsList;
